import React, { useEffect, useState } from 'react';
import { Palette, Save, RefreshCw, Type, Contrast, Sparkles, LayoutTemplate, Home } from 'lucide-react';
import { api } from '@/lib/api';
import { TextField, Toggle, Select } from '@/components/admin/shared';
import HomepageDirector from '@/components/admin/HomepageDirector';

const FONT_SERIF_OPTS = [
  { value: 'Serif Pro',       label: 'Serif Pro (default)' },
  { value: 'Playfair Display', label: 'Playfair Display' },
  { value: 'Crimson Text',    label: 'Crimson Text' },
];
const FONT_SANS_OPTS = [
  { value: 'Figtree',    label: 'Figtree (default)' },
  { value: 'Montserrat', label: 'Montserrat' },
  { value: 'Fredoka',    label: 'Fredoka' },
];
const FONT_MONO_OPTS = [
  { value: 'Source Code Pro', label: 'Source Code Pro (default)' },
  { value: 'Roboto Mono',     label: 'Roboto Mono' },
];
const BUTTON_STYLE_OPTS = [
  { value: 'solid',   label: 'Solid' },
  { value: 'outline', label: 'Outline' },
  { value: 'gold',    label: 'Gold' },
];

function ColorField({ label, value, onChange }) {
  return (
    <div className="pg-input rounded-[var(--pg-radius-sm)] p-2 flex items-center gap-2">
      <input type="color" value={/^#/.test(value || '') ? value : '#c9a24a'} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 rounded" />
      <div className="flex-1 min-w-0">
        <label className="text-[10px] font-mono tracking-widest text-[var(--pg-ink-faint)] uppercase">{label}</label>
        <input value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-transparent text-xs text-[var(--pg-ink)] focus:outline-none" />
      </div>
    </div>
  );
}

function ThemeSection({ form, set }) {
  return (
    <>
      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px] flex items-center gap-2"><Type size={12} /> TYPOGRAPHY</h4>
        <div className="grid grid-cols-3 gap-3">
          <Select label="Serif font" value={form.font_family_serif} onChange={set('font_family_serif')} options={FONT_SERIF_OPTS} />
          <Select label="Sans font" value={form.font_family_sans} onChange={set('font_family_sans')} options={FONT_SANS_OPTS} />
          <Select label="Mono font" value={form.font_family_mono} onChange={set('font_family_mono')} options={FONT_MONO_OPTS} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <TextField label="Base font size (px)" type="number" value={form.base_font_size_px} onChange={set('base_font_size_px')} />
          <TextField label="Heading weight" type="number" value={form.heading_font_weight} onChange={set('heading_font_weight')} />
          <TextField label="Body weight" type="number" value={form.body_font_weight} onChange={set('body_font_weight')} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Line height" type="number" value={form.line_height} onChange={set('line_height')} />
          <TextField label="Letter spacing (em)" value={form.letter_spacing} onChange={set('letter_spacing')} />
        </div>
      </section>

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px] flex items-center gap-2"><Palette size={12} /> COLORS</h4>
        <div className="grid grid-cols-2 gap-2">
          <ColorField label="Primary"    value={form.color_primary}   onChange={set('color_primary')} />
          <ColorField label="Secondary"  value={form.color_secondary} onChange={set('color_secondary')} />
          <ColorField label="Accent"     value={form.color_accent}    onChange={set('color_accent')} />
          <ColorField label="Ink"        value={form.color_ink}       onChange={set('color_ink')} />
          <ColorField label="Ink muted"  value={form.color_ink_muted} onChange={set('color_ink_muted')} />
          <ColorField label="Ink faint"  value={form.color_ink_faint} onChange={set('color_ink_faint')} />
          <ColorField label="Background" value={form.color_bg}        onChange={set('color_bg')} />
          <ColorField label="Elevated"   value={form.color_bg_elev}   onChange={set('color_bg_elev')} />
        </div>
        <TextField label="Border (CSS)" value={form.color_border} onChange={set('color_border')} placeholder="rgba(242,238,227,0.12)" />
      </section>

      <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
        <h4 className="pg-label text-[10px]">SHAPES & SPACING</h4>
        <div className="grid grid-cols-3 gap-3">
          <Select label="Button style" value={form.button_style} onChange={set('button_style')} options={BUTTON_STYLE_OPTS} />
          <TextField label="Button radius" value={form.button_radius} onChange={set('button_radius')} placeholder="10px" />
          <TextField label="Global spacing" value={form.global_spacing} onChange={set('global_spacing')} placeholder="16px" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <TextField label="Radius sm" value={form.radius_sm} onChange={set('radius_sm')} />
          <TextField label="Radius md" value={form.radius_md} onChange={set('radius_md')} />
          <TextField label="Radius lg" value={form.radius_lg} onChange={set('radius_lg')} />
        </div>
        <TextField label="Shadow sm" value={form.shadow_sm} onChange={set('shadow_sm')} />
        <TextField label="Shadow md" value={form.shadow_md} onChange={set('shadow_md')} />
      </section>
    </>
  );
}

function ReadabilitySection({ form, set }) {
  return (
    <section className="pg-admin-card rounded-[var(--pg-radius-md)] p-5 space-y-3">
      <h4 className="pg-label text-[10px] flex items-center gap-2"><Contrast size={12} /> READABILITY</h4>
      <Toggle label="Readability controls enabled" value={form.enabled} onChange={set('enabled')} />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Font brightness % (60–160)" type="number" value={form.font_brightness_pct} onChange={set('font_brightness_pct')} />
        <TextField label="Font contrast boost % (0–60)" type="number" value={form.font_contrast_boost_pct} onChange={set('font_contrast_boost_pct')} />
      </div>
      <Toggle label="Background overlay enabled" value={form.overlay_enabled} onChange={set('overlay_enabled')} />
      <div className="grid grid-cols-2 gap-3">
        <TextField label="Overlay color (CSS)" value={form.overlay_color} onChange={set('overlay_color')} />
        <TextField label="Overlay opacity % (0–90)" type="number" value={form.overlay_opacity_pct} onChange={set('overlay_opacity_pct')} />
      </div>
      <TextField label="Background blur (px, 0–32)" type="number" value={form.background_blur_px} onChange={set('background_blur_px')} />
      <Toggle label="Dark pills for text over bright backgrounds" value={form.pill_backgrounds} onChange={set('pill_backgrounds')} />
    </section>
  );
}

export default function ExperienceManager() {
  const [tab, setTab] = useState('theme');
  const [theme, setTheme] = useState(null);
  const [readability, setReadability] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = async () => {
    try {
      const [t, r, tpl] = await Promise.all([
        api.get('/admin/theme'),
        api.get('/admin/readability'),
        api.get('/admin/templates'),
      ]);
      setTheme(t.data); setReadability(r.data); setTemplates(tpl.data || []);
    } catch (e) { setMsg(e?.response?.data?.detail || 'Load failed'); }
  };
  useEffect(() => { load(); }, []);

  const setTheme_ = (k) => (v) => setTheme((f) => ({ ...f, [k]: v }));
  const setRead_  = (k) => (v) => setReadability((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      if (tab === 'theme') {
        const payload = { ...theme }; delete payload.id; delete payload.updated_at;
        const { data } = await api.put('/admin/theme', payload);
        setTheme(data);
      } else if (tab === 'readability') {
        const payload = { ...readability }; delete payload.id; delete payload.updated_at;
        payload.font_brightness_pct = Number(payload.font_brightness_pct) || 100;
        payload.font_contrast_boost_pct = Number(payload.font_contrast_boost_pct) || 0;
        payload.overlay_opacity_pct = Number(payload.overlay_opacity_pct) || 0;
        payload.background_blur_px = Number(payload.background_blur_px) || 0;
        const { data } = await api.put('/admin/readability', payload);
        setReadability(data);
      }
      setMsg('Saved. Changes apply site-wide immediately.');
      // Broadcast so SiteSettingsApplier can re-fetch
      window.dispatchEvent(new CustomEvent('pg:experience-updated'));
    } catch (e) { setMsg(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  const setTemplateDefault = async (t) => {
    try {
      const { data } = await api.put(`/admin/templates/${t.id}`, { is_default: true });
      setMsg(`Set as default for ${t.kind}: ${data.label}`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Update failed'); }
  };

  const groupedTemplates = React.useMemo(() => {
    const groups = { channel: [], product: [], landing: [], collection: [] };
    (templates || []).forEach((t) => { (groups[t.kind] = groups[t.kind] || []).push(t); });
    return groups;
  }, [templates]);

  if (!theme || !readability) return <div className="p-4 text-xs font-mono text-[var(--pg-ink-faint)]">Loading Experience Manager…</div>;

  return (
    <div className="space-y-4" data-testid="experience-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-[var(--pg-gold-2)]" />
          <h3 className="font-serif text-lg text-[var(--pg-ink)]">Experience Manager</h3>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="pg-btn-ghost px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2"><RefreshCw size={14} /> Reload</button>
          {tab !== 'templates' && tab !== 'homepage_director' && (
            <button data-testid="experience-save" onClick={save} disabled={busy} className="pg-btn-primary px-3 py-2 rounded-[var(--pg-radius-sm)] text-xs flex items-center gap-2 disabled:opacity-60">
              <Save size={14} /> {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="experience-tabs">
        {[
          { k: 'homepage_director', l: 'Homepage Director' },
          { k: 'theme', l: 'Theme' },
          { k: 'readability', l: 'Readability' },
          { k: 'templates', l: 'Templates' },
        ].map((t) => (
          <button key={t.k} type="button" onClick={() => setTab(t.k)}
                  data-testid={`experience-tab-${t.k}`}
                  className="px-3 py-1.5 rounded-full border text-[10px] font-mono tracking-[0.24em] uppercase"
                  style={{
                    background: tab === t.k ? 'rgba(226,199,122,0.14)' : 'transparent',
                    borderColor: tab === t.k ? 'rgba(226,199,122,0.6)' : 'var(--pg-border-2)',
                    color: tab === t.k ? 'var(--pg-gold-2)' : 'var(--pg-ink-muted)',
                  }}>
            {t.l}
          </button>
        ))}
      </div>

      {msg && <div className="pg-input rounded-[var(--pg-radius-sm)] px-3 py-2 text-xs text-[var(--pg-ink)]">{msg}</div>}

      {tab === 'homepage_director' && <HomepageDirector />}
      {tab === 'theme' && <ThemeSection form={theme} set={setTheme_} />}
      {tab === 'readability' && <ReadabilitySection form={readability} set={setRead_} />}

      {tab === 'templates' && (
        <div className="space-y-6">
          {Object.entries(groupedTemplates).map(([kind, items]) => items.length === 0 ? null : (
            <section key={kind} className="pg-admin-card rounded-[var(--pg-radius-md)] p-5">
              <h4 className="pg-label text-[10px] mb-3 flex items-center gap-2">
                <LayoutTemplate size={12} /> {kind.toUpperCase()} TEMPLATES ({items.length})
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((t) => (
                  <div key={t.id} className="pg-input rounded-[var(--pg-radius-sm)] p-4 flex flex-col gap-2" data-testid={`template-card-${t.key}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-serif text-base text-[var(--pg-ink)]">{t.label}</p>
                      {t.is_default && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-mono tracking-widest uppercase bg-[rgba(226,199,122,0.14)] border border-[rgba(226,199,122,0.4)] text-[var(--pg-gold-2)]">Default</span>
                      )}
                    </div>
                    <p className="text-[11px] text-[var(--pg-ink-muted)] leading-relaxed">{t.description}</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(t.tags || []).map((tag) => (
                        <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full font-mono tracking-widest bg-[rgba(255,255,255,0.04)] border border-[var(--pg-border-2)] text-[var(--pg-ink-faint)]">{tag}</span>
                      ))}
                    </div>
                    <div className="mt-auto pt-2 flex items-center gap-2">
                      {!t.is_default && (
                        <button onClick={() => setTemplateDefault(t)} className="pg-btn-ghost px-2 py-1 rounded-[var(--pg-radius-sm)] text-[11px]" data-testid={`template-set-default-${t.key}`}>
                          Set as default
                        </button>
                      )}
                      <span className="text-[10px] font-mono text-[var(--pg-ink-faint)] ml-auto">{t.is_builtin ? 'built-in' : 'custom'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
          <p className="text-[11px] text-[var(--pg-ink-faint)] font-mono">Templates apply presentation only. Underlying CMS content, URLs, analytics, and commerce functionality are preserved. Assign templates per-item from the Channels or Vault editors (below “Presentation Override”).</p>
        </div>
      )}
    </div>
  );
}
